import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import i18n from "@/i18n";

const API = `${process.env.REACT_APP_API_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const resetLanguageToDefault = useCallback(() => {
    if (i18n.language?.split("-")[0] !== "id") {
      i18n.changeLanguage("id");
    }
  }, []);

  const syncLanguage = useCallback((userData) => {
    if (userData?.language) {
      const lang = userData.language.split("-")[0];
      if (lang !== i18n.language?.split("-")[0]) {
        i18n.changeLanguage(lang);
      }
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      syncLanguage(res.data);
      return res.data;
    } catch {
      setUser(null);
      resetLanguageToDefault();
      return null;
    }
  }, [resetLanguageToDefault, syncLanguage]);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip /me check.
    // AuthCallback will exchange the code and establish the session first.
    const path = window.location.pathname;
    if (path === "/auth/callback") {
      setLoading(false);
      return;
    }
    try {
      await refreshUser();
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    resetLanguageToDefault();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
